import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import NotificationSoundProvider from './components/NotificationSoundProvider';
import ChatWidget from './components/ChatWidget';
import './App.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import Abandoned from './pages/Abandoned';
import Reports from './pages/Reports';
import History from './pages/History';
import Settings from './pages/Settings';
import UsersPage from './pages/UsersNew';
import UserEdit from './pages/UserEdit';
import Clients from './pages/Clients';
import Livraisons from './pages/Livraisons';
import Organizations from './pages/Organizations';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import CommissionReport from './pages/CommissionReport';
import Vendeurs from './pages/Vendeurs';
import Products from './pages/Products';
import ClaimOrder from './pages/ClaimOrder';
import ConfirmOrder from './pages/ConfirmOrder';
import Automatisations from './pages/Automatisations';
import VendorProducts from './pages/VendorProducts';
import VendorProductsAdmin from './pages/VendorProductsAdmin';
import Messages from './pages/Messages';
import SocialInbox from './pages/SocialInbox';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/connexion" replace />;
    }

    return children;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner" />
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Dashboard or Redirect for vendors
const DashboardOrRedirect = () => {
    const { user } = useAuth();
    
    // Vendors (external suppliers) should be redirected to orders page
    if (user?.role === 'vendeur') {
        return <Navigate to="/commandes" replace />;
    }
    
    // Admin, super_admin, office_agent see dashboard
    return <Dashboard />;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/connexion"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />
            <Route
                path="/inscription"
                element={
                    <PublicRoute>
                        <Register />
                    </PublicRoute>
                }
            />
            
            {/* Claim Order - Protected but with custom handling inside component */}
            <Route
                path="/claim/:orderId"
                element={
                    <ProtectedRoute>
                        <ClaimOrder />
                    </ProtectedRoute>
                }
            />
            
            {/* Order Confirmation - PUBLIC page for customers */}
            <Route
                path="/confirmer/:token"
                element={<ConfirmOrder />}
            />

            {/* Protected Routes */}
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <DashboardOrRedirect />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/commandes"
                element={
                    <ProtectedRoute>
                        <Orders />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/abandonnees"
                element={
                    <ProtectedRoute>
                        <Abandoned />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/rapports"
                element={
                    <ProtectedRoute>
                        <Reports />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/commissions"
                element={
                    <ProtectedRoute>
                        <CommissionReport />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/utilisateurs"
                element={
                    <ProtectedRoute>
                        <UsersPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/utilisateurs/:userId"
                element={
                    <ProtectedRoute>
                        <UserEdit />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/vendeurs"
                element={
                    <ProtectedRoute>
                        <Vendeurs />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/historique"
                element={
                    <ProtectedRoute>
                        <History />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/clients"
                element={
                    <ProtectedRoute>
                        <Clients />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/livraisons"
                element={
                    <ProtectedRoute>
                        <Livraisons />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/organisations"
                element={
                    <ProtectedRoute>
                        <Organizations />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/super-admin"
                element={
                    <ProtectedRoute>
                        <SuperAdminDashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/produits"
                element={
                    <ProtectedRoute>
                        <Products />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/automatisations"
                element={
                    <ProtectedRoute>
                        <Automatisations />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/parametres"
                element={
                    <ProtectedRoute>
                        <Settings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/mes-produits"
                element={
                    <ProtectedRoute>
                        <VendorProducts />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/produits-vendeurs"
                element={
                    <ProtectedRoute>
                        <VendorProductsAdmin />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/messages"
                element={
                    <ProtectedRoute>
                        <Messages />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/social-inbox"
                element={
                    <ProtectedRoute>
                        <SocialInbox />
                    </ProtectedRoute>
                }
            />

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <NotificationSoundProvider>
                    <AppRoutes />
                    <ChatWidget />
                    <Toaster position="top-right" richColors closeButton />
                    <PWAInstallPrompt />
                </NotificationSoundProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
